import { z } from "zod";

// Transport providers are independent of integration catalog keys.
export const connectedChatProviderSchema = z
  .enum(["slack", "linq"])
  .describe("ConnectedChatProvider");

export const publisherInternalFindConnectedChatConnectionBodySchema = z
  .object({
    provider: connectedChatProviderSchema,
    nangoConnectionId: z.string().min(1),
    externalWorkspaceId: z.string().min(1),
  })
  .describe("PublisherInternalFindConnectedChatConnectionBody");

export const publisherInternalFindConnectedChatConnectionResponseSchema = z
  .object({
    nangoConnectionId: z.string().min(1),
  })
  .describe("PublisherInternalFindConnectedChatConnectionResponse");

export const connectedChatActionRejectionReasonSchema = z
  .enum([
    "connection_not_found",
    "actor_not_found",
    "actor_not_member",
    "invited_user_not_found",
  ])
  .describe("ConnectedChatActionRejectionReason");

export const publisherInternalValidateTokenBodySchema = z.object({
  token: z.string().min(1),
});

export const publisherInternalValidateTokenResponseSchema = z.object({
  user: z
    .object({
      userId: z.string(),
      email: z.string(),
      name: z.string(),
    })
    .nullable(),
});

export const publisherInternalCheckAccessBodySchema = z.object({
  userId: z.uuid(),
  projectId: z.uuid(),
});

export const publisherInternalCheckAccessResponseSchema = z.object({
  hasAccess: z.boolean(),
  isSuperuser: z.boolean(),
  workspaceId: z.string().nullable(),
  email: z.string().nullable(),
  name: z.string().nullable(),
});

export const publisherInternalHydrateUserBodySchema = z.object({
  userId: z.uuid(),
});

export const publisherInternalHydrateUserResponseSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string(),
  isSuperuser: z.boolean(),
});

// NOTE (Kevin, 2026-08-27, REPL-31280): The Slack connected-chat runtime body,
// kept strict. Slack carries Nango/workspace/actor identifiers. linq (Messages)
// is invisible transport whose actor is resolved from the sender's phone
// number, so it has its own strict arm on the access body
// (packages/schemas/bedrockApi/connectedChat.ts) rather than optionalizing
// these required Slack fields.
export const publisherInternalConnectedChatRuntimeBodySchema = z
  .object({
    provider: z.literal("slack"),
    nangoConnectionId: z.string().min(1),
    externalWorkspaceId: z.string().min(1),
    externalActorId: z.string().min(1),
    externalActorEmail: z.email().optional(),
    externalChannelId: z.string().min(1).optional(),
  })
  .describe("PublisherInternalConnectedChatRuntimeBody");

export const publisherInternalApproveConnectedChatInviteBodySchema = z
  .object({
    actorUserId: z.uuid(),
    provider: connectedChatProviderSchema,
    nangoConnectionId: z.string().min(1),
    externalWorkspaceId: z.string().min(1),
    externalActorEmail: z.email(),
  })
  .describe("PublisherInternalApproveConnectedChatInviteBody");

export const publisherInternalApproveConnectedChatInviteResponseSchema = z
  .discriminatedUnion("result", [
    z.object({
      result: z.literal("approved"),
      invitedUserId: z.uuid(),
    }),
    z.object({
      result: z.literal("rejected"),
      reason: connectedChatActionRejectionReasonSchema,
    }),
  ])
  .describe("PublisherInternalApproveConnectedChatInviteResponse");
